/**
 * Utility functions for the Form Builder.
 * Handles creation, normalization, validation, and manipulation of form data structures.
 */
import type {
  Condition,
  ConditionalControl,
  ControlOption,
  FormControl,
  FormDefinition,
  FormSection,
  ValidationIssue,
} from './types';

const nowIso = () => new Date().toISOString();

const clone = <T>(value: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

// --- Constants ---

export const DATA_LOOKUP_DATA_TYPES = [
  'vehicle_category',
  'vehicle',
  'job',
  'job_category',
  'task_type',
  'user',
  'team',
  'sa_schemes',
  'sa_properties',
  'sa_teams',
  'sa_team_members',
  'sa_projects',
];

export const CONTROL_SPAN_OPTIONS = [
  { label: 'Full Width', value: '12' },
  { label: 'Half Width', value: '6' },
  { label: 'Third Width', value: '4' },
  { label: 'Quarter Width', value: '3' },
];

export const CONDITIONAL_CONTROL_TYPES = [
  { label: 'Text', value: 'text' },
  { label: 'Textarea', value: 'textarea' },
  { label: 'Number', value: 'number' },
  { label: 'Date', value: 'date' },
  { label: 'Time', value: 'time' },
  { label: 'Data lookup', value: 'data_lookup' },
  { label: 'Dropdown', value: 'option' },
  { label: 'Radio', value: 'radio' },
  { label: 'Checkbox', value: 'checkbox' },
  { label: 'Switch', value: 'switch' },
  { label: 'Image Upload', value: 's3_image' },
  { label: 'Signature', value: 'signature' },
];

// --- Conditional field helpers ---

export const createConditionalControl = (): ConditionalControl => ({
  q_id: '',
  control_name: '',
  control_type: 'text',
  control_description: '',
  control_span_length: '12',
  required: false,
  min: '',
  max: '',
  options: [],
  validations: [],
  value: null,
  s3_bucket_name: '',
  s3_folder_name: '',
});

export const createCondition = (): Condition => ({
  condition_name: '',
  condition_order: 0,
  next_q_id: null,
  end_form: false,
  conditionalControls: [],
});

// --- Factory functions ---

export const createEmptyFormDefinition = (userId?: string | number): FormDefinition => ({
  id: null,
  name: '',
  description: '',
  image_url: '',
  rev_num: null,
  created_by: userId ?? '',
  created_time: nowIso(),
  updated_by: '',
  updated_time: '',
  is_delete: false,
  form_type_id: null,
  layout: 'Horizontal',
  is_dynamic: false,
  form: false,
  is_system: false,
  sections: [],
  compliant: false,
  can_take_action: false,
  is_compliant_submitted: false,
});

export const createSection = (
  name: string,
  existingSections: FormSection[],
  userId?: string | number,
): FormSection => {
  const maxSectionNumber = existingSections.reduce(
    (max, section) => Math.max(max, section.section_number ?? 0),
    0,
  );

  return {
    id: null,
    rev_num: null,
    sequence: existingSections.length + 1,
    section_name: name,
    section_description: null,
    is_table_view: null,
    is_repeatable: false,
    max_repeat: 100,
    form_id: null,
    created_by: userId ?? '',
    created_time: nowIso(),
    updated_by: '',
    updated_time: '',
    is_active: true,
    section_number: maxSectionNumber + 1,
    controls: [],
  };
};

const buildDefaultValidations = (controlType: string, required: boolean) => {
  const validations: any[] = [];
  if (required) {
    validations.push({
      name: 'required',
      label: 'Required',
      option: false,
      description: 'Please complete the required field before continuing',
    });
  }

  if (controlType === 's3_image') {
    validations.push(
      {
        name: 'size',
        label: 'Size',
        option: true,
        description:
          'The correct input size for this field has not been met, please change the value and continue',
      },
      {
        name: 'max',
        label: 'Max files',
        option: true,
        description:
          'The maximum number of items/images has been exceeded, please remove some and continue',
      },
      {
        name: 'min',
        label: 'Min files',
        option: true,
        description:
          'The minimum number of items/images has not been met, please add more and continue',
      },
    );
  }

  return validations;
};

const getDefaultMinMax = (controlType: string) => {
  if (controlType === 's3_image') return { min: '1', max: '10' };
  if (controlType === 'textarea') return { min: '3', max: '250' };
  if (controlType === 'long_textarea') return { min: '3', max: '1000' };
  return { min: '', max: '' };
};

const getDefaultStorage = (controlType: string) => {
  if (controlType === 's3_image' || controlType === 'signature') {
    // Bucket and per-client folder are injected at build time by the frontend
    // pipeline (VITE_WORKS_LITE_UPLOAD_BUCKET / VITE_WORKS_LITE_UPLOAD_FOLDER), composed per client
    // in Terraform. Empty fallbacks keep local dev and untouched builds working.
    return {
      s3_bucket_name: import.meta.env.VITE_WORKS_LITE_UPLOAD_BUCKET || '',
      s3_folder_name: import.meta.env.VITE_WORKS_LITE_UPLOAD_FOLDER || '',
    };
  }
  return { s3_bucket_name: '', s3_folder_name: '' };
};

export const getDefaultStorageForType = (controlType: string) => getDefaultStorage(controlType);

const isOptionControl = (controlType: string) =>
  ['option', 'radio', 'checkbox', 'switch'].includes(controlType);

export const getNextQuestionId = (sections: FormSection[]) => {
  const numericIds = sections
    .flatMap((section) => section.controls)
    .map((control) => parseInt(String(control.q_id), 10))
    .filter((value) => !Number.isNaN(value));

  const maxId = numericIds.length ? Math.max(...numericIds) : 1000;
  return String(maxId + 1);
};

export const createControl = (
  controlType: string,
  label: string,
  sections: FormSection[],
  defaults?: Partial<FormControl>,
): FormControl => {
  const q_id = defaults?.q_id ?? getNextQuestionId(sections);
  const { min, max } = getDefaultMinMax(controlType);
  const { s3_bucket_name, s3_folder_name } = getDefaultStorage(controlType);
  const required = defaults?.required ?? false;

  const base: FormControl = {
    q_id,
    mapping_key: '',
    control_name: label,
    control_type: controlType,
    control_description: '',
    control_span_length: '12',
    conditional_type: controlType === 'conditional_field' ? 'radio' : '',
    required,
    min: defaults?.min ?? min,
    max: defaults?.max ?? max,
    order: null,
    prev_q_id: null,
    next_q_id: null,
    options: [],
    nested_controls: [],
    nestedControls: [],
    conditions: controlType === 'conditional_field' ? [] : [],
    validations: defaults?.validations ?? buildDefaultValidations(controlType, required),
    value: null,
    s3_bucket_name,
    s3_folder_name,
    calculated_type: '',
    field1_value: '',
    field2_value: '',
    operator: '',
    help_text: '',
    is_repeatable: false,
    min_repeat: 0,
    max_repeat: 0,
    data_type: '',
    is_multi_select: false,
    group_name: '',
    group_description: '',
    group_controls: controlType === 'group_controls',
  };

  const control = { ...base, ...defaults };
  if (controlType === 'conditional_field') {
    control.next_q_id = null;
  }
  return control;
};

// --- Normalization ---

export const normalizeFormDefinition = (form: FormDefinition): FormDefinition => {
  const next = clone(form);
  next.sections = (next.sections || []).map((section, index) => ({
    ...section,
    sequence: section.sequence ?? index + 1,
    section_description: section.section_description ?? null,
    is_table_view: section.is_table_view ?? null,
    controls: (Array.isArray(section.controls) ? section.controls : []).map((control) => {
      const normalized = {
        ...control,
        options: control.options || [],
        // Coerce end_form to a real boolean so forms saved before this flag existed
        // (undefined) and any truthy JSON value normalise to the same shape.
        end_form: control.end_form === true,
        conditions: (control.conditions || []).map((condition: any) => ({
          ...condition,
          end_form: condition?.end_form === true,
        })),
        validations: control.validations || [],
        nested_controls: control.nested_controls || [],
        nestedControls: control.nestedControls || [],
        required: control.required ?? false,
        mapping_key: control.mapping_key ?? '',
        control_span_length: control.control_span_length || '12',
        s3_bucket_name: control.s3_bucket_name || '',
        s3_folder_name: control.s3_folder_name || '',
      };

      const applyStorageDefaults = (c: FormControl): FormControl => {
        const defaults = getDefaultStorage(c.control_type);
        const withStorage: FormControl = {
          ...c,
          s3_bucket_name: c.s3_bucket_name || defaults.s3_bucket_name,
          s3_folder_name: c.s3_folder_name || defaults.s3_folder_name,
        };

        if (Array.isArray(withStorage.nested_controls)) {
          withStorage.nested_controls = withStorage.nested_controls.map((nested) =>
            applyStorageDefaults(nested as FormControl),
          );
        }
        if (Array.isArray(withStorage.nestedControls)) {
          withStorage.nestedControls = withStorage.nestedControls.map((nested) =>
            applyStorageDefaults(nested as FormControl),
          );
        }
        if (Array.isArray(withStorage.conditions)) {
          withStorage.conditions = withStorage.conditions.map((condition: any) => ({
            ...condition,
            conditionalControls: (condition?.conditionalControls || []).map((nested: FormControl) =>
              applyStorageDefaults(nested),
            ),
          }));
        }
        return withStorage;
      };

      if (normalized.control_type === 'conditional_field') {
        normalized.next_q_id = null;
      }

      return applyStorageDefaults(normalized);
    }),
  }));

  return next;
};

// --- Link Syncing ---

const normalizeNextQuestionId = (value: FormControl['next_q_id']) => {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

export const getDefaultNextQuestionId = (
  sections: FormSection[],
  sectionIndex: number,
  controlIndex: number,
) => {
  const section = sections[sectionIndex];
  if (!section) return null;
  const nextControl = section.controls?.[controlIndex + 1];
  if (nextControl) return nextControl.q_id;
  const nextSection = sections[sectionIndex + 1];
  return nextSection?.controls?.[0]?.q_id ?? null;
};

/**
 * Keeps each condition's branch target consistent with its end_form flag.
 *
 * A branch marked as ending the form must persist `next_q_id: null` — that null
 * is what makes the runtime finish the form instead of offering Next. Branch
 * targets are otherwise left alone (unlike control-level links, they are never
 * auto-defaulted to the following question).
 */
const syncConditionLinks = (control: FormControl, validQuestionIds: Set<string>): Condition[] => {
  if (!Array.isArray(control.conditions)) return [];

  return control.conditions.map((condition) => {
    if (condition?.end_form === true) {
      return { ...condition, next_q_id: null };
    }
    // Clear a branch target whose question has been deleted. Leaving the stale id
    // would strand the runtime, which looks the target up by q_id and silently
    // does nothing when it is missing.
    const target = normalizeNextQuestionId(condition?.next_q_id ?? null);
    if (target && !validQuestionIds.has(target)) {
      return { ...condition, next_q_id: null };
    }
    return condition;
  });
};

const syncControlLinksForSection = (
  sections: FormSection[],
  sectionIndex: number,
  validQuestionIds: Set<string>,
): FormSection => {
  const section = sections[sectionIndex];
  if (!section) return { ...section, controls: [] };

  const controls = section.controls.map((control, index, list) => {
    const prev = list[index - 1];
    const defaultNext = getDefaultNextQuestionId(sections, sectionIndex, index);
    const customNext = normalizeNextQuestionId(control.next_q_id);
    // Drop a custom next_q_id that points at a question no longer in the form
    // (e.g. the target was deleted) and fall back to the natural next control,
    // otherwise validation rejects the dangling reference on save.
    const resolvedCustomNext = customNext && validQuestionIds.has(customNext) ? customNext : null;

    // An author-marked end-of-form question keeps next_q_id null rather than
    // being relinked to the following question, which is what makes the runtime
    // show Save instead of Next. Without this the null would be overwritten by
    // defaultNext and the form would carry on.
    const endsForm = control.end_form === true;

    return {
      ...control,
      prev_q_id: prev ? prev.q_id : null,
      next_q_id:
        control.control_type === 'conditional_field' || endsForm
          ? null
          : (resolvedCustomNext ?? defaultNext),
      conditions: syncConditionLinks(control, validQuestionIds),
    };
  });

  return { ...section, controls };
};

export const syncFormControlLinks = (form: FormDefinition): FormDefinition => {
  const next = clone(form);
  const sections = next.sections || [];

  // Collect every question id currently in the form so dangling next_q_id
  // references (to deleted questions) can be cleared during sync.
  const validQuestionIds = new Set<string>();
  sections.forEach((section) => {
    section.controls?.forEach((control) => {
      const id = normalizeNextQuestionId(control.q_id != null ? String(control.q_id) : null);
      if (id) validQuestionIds.add(id);
    });
  });

  next.sections = sections.map((_section, sectionIndex) =>
    syncControlLinksForSection(sections, sectionIndex, validQuestionIds),
  );
  return next;
};

// --- Validation ---

export const validateFormDefinition = (form: FormDefinition): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const questionIdSet = new Set<string>();
  // Maps a normalized mapping key -> the paths of every control that uses it, so
  // duplicates can be surfaced on each offending control.
  const mappingKeyPaths = new Map<string, string[]>();

  const addQuestionId = (value?: string | number | null) => {
    if (value === null || value === undefined) return;
    const normalized = String(value).trim();
    if (normalized) questionIdSet.add(normalized);
  };

  const collectQuestionIds = (controls: FormControl[] = []) => {
    controls.forEach((control) => {
      addQuestionId(control.q_id);
      if (Array.isArray(control.nested_controls)) {
        collectQuestionIds(control.nested_controls as FormControl[]);
      }
      if (Array.isArray(control.conditions)) {
        control.conditions.forEach((condition: any) => {
          (condition?.conditionalControls || []).forEach((nested: FormControl) => {
            addQuestionId(nested.q_id);
            if (Array.isArray(nested.nested_controls)) {
              collectQuestionIds(nested.nested_controls as FormControl[]);
            }
          });
        });
      }
    });
  };

  // Mapping keys must be unique within a form so CORE's per-key field mapping is
  // unambiguous. Comparison is case- and whitespace-insensitive; empty keys are
  // ignored (the field is optional). Walks the full control tree, including
  // nested_controls and conditional-branch controls.
  const collectMappingKeys = (controls: FormControl[] = [], basePath: string) => {
    controls.forEach((control, controlIndex) => {
      const controlPath = `${basePath}.${controlIndex}`;
      const mappingKey = String(control.mapping_key ?? '')
        .trim()
        .toLowerCase();
      if (mappingKey) {
        mappingKeyPaths.set(mappingKey, [...(mappingKeyPaths.get(mappingKey) || []), controlPath]);
      }
      if (Array.isArray(control.nested_controls)) {
        collectMappingKeys(
          control.nested_controls as FormControl[],
          `${controlPath}.nested_controls`,
        );
      }
      if (Array.isArray(control.conditions)) {
        control.conditions.forEach((condition: any, conditionIndex: number) => {
          collectMappingKeys(
            condition?.conditionalControls || [],
            `${controlPath}.conditions.${conditionIndex}.conditionalControls`,
          );
        });
      }
    });
  };

  form.sections?.forEach((section) => collectQuestionIds(section.controls || []));

  form.sections?.forEach((section, sectionIndex) =>
    collectMappingKeys(section.controls || [], `sections.${sectionIndex}.controls`),
  );
  mappingKeyPaths.forEach((paths, mappingKey) => {
    if (paths.length <= 1) return;
    paths.forEach((path) => {
      issues.push({
        path: `${path}.mapping_key`,
        message: `Mapping key "${mappingKey}" is already used in this form.`,
      });
    });
  });

  if (!form.name?.trim()) {
    issues.push({ path: 'name', message: 'Form name is required.' });
  }
  if (!form.form_type_id) {
    issues.push({ path: 'form_type_id', message: 'Form type is required.' });
  }
  if (!form.layout) {
    issues.push({ path: 'layout', message: 'Layout is required.' });
  }

  form.sections?.forEach((section, sectionIndex) => {
    if (!section.section_name?.trim()) {
      issues.push({
        path: `sections.${sectionIndex}.section_name`,
        message: 'Section name is required.',
      });
    }

    if (section.is_repeatable) {
      const maxRepeat = Number(section.max_repeat ?? 0);
      if (!Number.isFinite(maxRepeat) || maxRepeat < 1 || maxRepeat > 100) {
        issues.push({
          path: `sections.${sectionIndex}.max_repeat`,
          message: 'Repeat limit must be between 1 and 100.',
        });
      }
    }

    section.controls?.forEach((control, controlIndex) => {
      const basePath = `sections.${sectionIndex}.controls.${controlIndex}`;
      if (!control.control_name?.trim()) {
        issues.push({ path: `${basePath}.control_name`, message: 'Field label is required.' });
      }
      if (!control.control_type) {
        issues.push({ path: `${basePath}.control_type`, message: 'Field type is required.' });
      }
      if (!control.q_id) {
        issues.push({ path: `${basePath}.q_id`, message: 'Question ID is required.' });
      }
      if (control.next_q_id) {
        const normalizedNext = String(control.next_q_id).trim();
        if (normalizedNext && !questionIdSet.has(normalizedNext)) {
          issues.push({
            path: `${basePath}.next_q_id`,
            message: 'Next question ID is not found in this form.',
          });
        }
      }
      // A question cannot both end the form and jump somewhere else.
      if (control.end_form === true && normalizeNextQuestionId(control.next_q_id)) {
        issues.push({
          path: `${basePath}.end_form`,
          message: 'A question cannot both end the form and have a next question.',
        });
      }
      // The very first question ending the form would make the form unanswerable.
      if (control.end_form === true && sectionIndex === 0 && controlIndex === 0) {
        issues.push({
          path: `${basePath}.end_form`,
          message: 'The first question cannot end the form.',
        });
      }
      if (
        isOptionControl(control.control_type) &&
        (!control.options || control.options.length === 0)
      ) {
        issues.push({ path: `${basePath}.options`, message: 'Provide at least one option.' });
      }
      if (control.control_type === 'conditional_field') {
        if (!control.conditional_type) {
          issues.push({
            path: `${basePath}.conditional_type`,
            message: 'Conditional type is required.',
          });
        }
        if (!control.conditions || control.conditions.length === 0) {
          issues.push({ path: `${basePath}.conditions`, message: 'Add at least one condition.' });
        }
        control.conditions?.forEach((condition, conditionIndex) => {
          const conditionPath = `${basePath}.conditions.${conditionIndex}`;
          const branchTarget = normalizeNextQuestionId(condition?.next_q_id ?? null);
          // Branch targets were previously unvalidated, so a deleted target could
          // be saved and silently strand the runtime (which looks the id up and
          // does nothing when absent).
          if (branchTarget && !questionIdSet.has(branchTarget)) {
            issues.push({
              path: `${conditionPath}.next_q_id`,
              message: 'Branch target question is not found in this form.',
            });
          }
          if (condition?.end_form === true && branchTarget) {
            issues.push({
              path: `${conditionPath}.end_form`,
              message: 'A branch cannot both end the form and go to a question.',
            });
          }
        });
      }
      if (control.control_type === 'data_lookup' && !control.data_type) {
        issues.push({
          path: `${basePath}.data_type`,
          message: `Data type is required. Supported: ${DATA_LOOKUP_DATA_TYPES.join(', ')}`,
        });
      }
      if (
        control.control_type === 'group_controls' &&
        (!control.nested_controls || control.nested_controls.length === 0)
      ) {
        issues.push({
          path: `${basePath}.nested_controls`,
          message: 'Group must contain at least one nested control.',
        });
      }
    });
  });

  return issues;
};

// --- Immutable update helpers ---

export const withUpdatedControl = (
  form: FormDefinition,
  sectionIndex: number,
  controlIndex: number,
  updater: (control: FormControl) => FormControl,
): FormDefinition => {
  const next = clone(form);
  const section = next.sections?.[sectionIndex];
  if (!section) return next;
  const controls = [...section.controls];
  const current = controls[controlIndex];
  if (!current) return next;
  controls[controlIndex] = updater(current);
  next.sections[sectionIndex] = { ...section, controls };
  return syncFormControlLinks(next);
};

export const withUpdatedSection = (
  form: FormDefinition,
  sectionIndex: number,
  updater: (section: FormSection) => FormSection,
): FormDefinition => {
  const next = clone(form);
  const section = next.sections?.[sectionIndex];
  if (!section) return next;
  next.sections[sectionIndex] = updater(section);
  return next;
};

export const insertControlInSection = (
  form: FormDefinition,
  sectionIndex: number,
  control: FormControl,
  position?: number,
): FormDefinition => {
  const next = clone(form);
  const section = next.sections?.[sectionIndex];
  if (!section) return next;

  if (position !== undefined && position >= 0 && position <= section.controls.length) {
    const controls = [...section.controls];
    controls.splice(position, 0, control);
    next.sections[sectionIndex] = { ...section, controls };
  } else {
    next.sections[sectionIndex] = { ...section, controls: [...section.controls, control] };
  }

  return syncFormControlLinks(next);
};

export const moveControlInSection = (
  form: FormDefinition,
  sectionIndex: number,
  fromIndex: number,
  toIndex: number,
): FormDefinition => {
  const next = clone(form);
  const section = next.sections?.[sectionIndex];
  if (!section) return next;
  const controls = [...section.controls];
  const [moved] = controls.splice(fromIndex, 1);
  controls.splice(toIndex, 0, moved);
  next.sections[sectionIndex] = { ...section, controls };
  return syncFormControlLinks(next);
};

export const removeControlFromSection = (
  form: FormDefinition,
  sectionIndex: number,
  controlIndex: number,
): FormDefinition => {
  const next = clone(form);
  const section = next.sections?.[sectionIndex];
  if (!section) return next;
  const controls = section.controls.filter((_, idx) => idx !== controlIndex);
  next.sections[sectionIndex] = { ...section, controls };
  return syncFormControlLinks(next);
};

export const updateOptions = (
  control: FormControl,
  updater: (options: ControlOption[]) => ControlOption[],
): FormControl => ({
  ...control,
  options: updater(control.options || []),
});

// --- Category helpers ---

export const categoryForType = (controlType: string) => {
  if (
    [
      'text',
      'textarea',
      'long_textarea',
      'number',
      'email',
      'tel',
      'url',
      'date',
      'time',
      'datetime-local',
    ].includes(controlType)
  ) {
    return 'Inputs';
  }
  if (
    ['radio', 'checkbox', 'option', 'switch', 'dynamic_dropdown', 'team_member_list'].includes(
      controlType,
    )
  ) {
    return 'Choice';
  }
  if (['s3_image', 'signature'].includes(controlType)) return 'Media';
  if (['group_controls', 'label'].includes(controlType)) return 'Layout';
  if (['conditional_field', 'calculated_field'].includes(controlType)) return 'Logic';
  return 'Other';
};

export const parseOptions = (value: unknown) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return [];
    }
  }
  return [];
};

export const formatControlType = (type?: string) => {
  if (!type) return '';
  return type
    .split('_')
    .map((part) =>
      part.length <= 2 ? part.toUpperCase() : `${part[0].toUpperCase()}${part.slice(1)}`,
    )
    .join(' ');
};

// --- Template builder ---

export const buildTemplatesFromConfigs = (
  configs: Array<{
    id: number;
    name: string;
    label: string;
    description?: string;
    option?: unknown;
    validations?: unknown;
  }>,
): {
  type: string;
  label: string;
  description?: string;
  category: string;
  defaults?: Partial<FormControl>;
}[] => {
  if (!configs.length) {
    return [
      { type: 'text', label: 'Short text', category: 'Inputs' },
      { type: 'textarea', label: 'Paragraph', category: 'Inputs' },
      { type: 'number', label: 'Number', category: 'Inputs' },
      { type: 'date', label: 'Date', category: 'Inputs' },
      { type: 'radio', label: 'Single choice', category: 'Choice' },
      { type: 'checkbox', label: 'Multiple choice', category: 'Choice' },
      { type: 'option', label: 'Dropdown', category: 'Choice' },
      { type: 's3_image', label: 'Photo upload', category: 'Media' },
      { type: 'signature', label: 'Signature', category: 'Media' },
      { type: 'label', label: 'Label', category: 'Layout' },
      { type: 'group_controls', label: 'Group', category: 'Layout' },
    ];
  }

  return configs.map((config) => ({
    type: config.name,
    label: config.label || config.name,
    description: config.description,
    category: categoryForType(config.name),
    defaults: {
      options: parseOptions(config.option),
      validations: parseOptions(config.validations),
    } as Partial<FormControl>,
  }));
};
